import React from 'react'

import { ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { useTranslation } from '@/core/i18n/useTranslation'
import { BillingInformationFormData } from '@/features/account/billing'
import { cn } from '@/shared/utils/helpers'

import { SubscriptionStep } from '../types'

export type SubscribeState = {
	plan?: string
	billingCycle?: string
	zonesCount?: number
	billingDraft?: BillingInformationFormData
}

interface SubscriptionStepperProps {
	currentStep: SubscriptionStep
	draft: SubscribeState
	onlyBackNavigation?: boolean
}

const steps: SubscriptionStep[] = ['choose', 'billing', 'payment']

const stepToPath: Record<SubscriptionStep, string> = {
	choose: '/subscribe',
	billing: '/subscribe/billing',
	payment: '/subscribe/payment',
}

export const SubscriptionStepper: React.FC<SubscriptionStepperProps> = ({
	currentStep,
	draft,
	onlyBackNavigation = true,
}) => {
	const { t } = useTranslation('common')
	const navigate = useNavigate()

	const currentIndex = steps.indexOf(currentStep)

	const getStepLabel = (step: SubscriptionStep): string => {
		switch (step) {
			case 'choose':
				return t('subscribe.steps.choose')
			case 'billing':
				return t('subscribe.steps.billing')
			case 'payment':
				return t('subscribe.steps.payment')
			default:
				return ''
		}
	}

	const handleClickOnStep = (step: SubscriptionStep, index: number) => {
		if (onlyBackNavigation && index >= currentIndex) return
		navigate(stepToPath[step], { state: draft })
	}

	return (
		<nav
			aria-label="Subscription steps"
			className="flex items-center justify-center gap-2"
		>
			{steps.map((step, index) => {
				const isActive = index === currentIndex
				const isPast = index < currentIndex
				const isFuture = index > currentIndex

				return (
					<React.Fragment key={step}>
						<button
							type="button"
							onClick={() => handleClickOnStep(step, index)}
							disabled={onlyBackNavigation ? !isPast : isActive}
							aria-current={isActive ? 'step' : undefined}
							className={cn(
								'px-4 py-2 rounded-full text-sm font-medium transition-colors',
								isActive
									? 'bg-card border border-border text-foreground'
									: isPast
										? 'text-foreground hover:bg-muted'
										: 'text-muted-foreground',
								(onlyBackNavigation ? !isPast : isActive) &&
									isFuture &&
									'cursor-not-allowed'
							)}
						>
							{getStepLabel(step)}
						</button>

						{index < steps.length - 1 && (
							<ArrowRight
								aria-hidden="true"
								className="h-4 w-4 text-muted-foreground"
							/>
						)}
					</React.Fragment>
				)
			})}
		</nav>
	)
}

export default SubscriptionStepper
